<script lang="ts">
    import {
        FullscreenControl,
        GeoJSONSource,
        LngLat,
        NavigationControl,
    } from "maplibre-gl";
    import { appManager } from "../../AppManager";
    import { afterUpdate } from "svelte";
    import { v4 as uuidv4 } from "uuid";
    import type { Feature, MultiPolygon, Point, Polygon } from "geojson";
    import * as turf from "@turf/turf";
    import DonutList from "./DonutList.svelte";
    import { ShortCutClassicLayer } from "../../map/layers/ShortCutClassicLayer";
    import { _ } from "svelte-i18n";
    import { ModalHook } from "../../shared/modals/ModalHook";
    import type { MatrixNewData } from "../../api/projects/data/matrix/MatrixNewData";
    import { MatrixTools } from "../../lib/matrices/MatrixTools";
    import type { MapManagerOptions } from "../../lib/maplibre/MapManagerOptions";
    import { MapManager } from "../../lib/maplibre/MapManager";
    import TinyHelp from "../../shared/help/TinyHelp.svelte";
    import { toolTip } from "../../shared/help/ToolTip";

    let hook: ModalHook<
        {
            zoom: number;
            center: LngLat;
        },
        { matrix: MatrixNewData; profile: string }
    > = new ModalHook<
        {
            zoom: number;
            center: LngLat;
        },
        { matrix: MatrixNewData; profile: string }
    >();
    appManager.ModalHooks.RandomPointsModal = hook;
    let randomLocationsApi = appManager.api.randomLocations;
    let open: boolean = false;
    let matrix: MatrixNewData = undefined;
    let profiles = appManager.profiles;
    let profile = "car.fast";
    let oneToMany = true;
    const oneToManyLabel = toolTip.New({
        key: "impact.scenario.randomPointsModal.onetomany.help.tooltip",
    });

    // create the map.
    let mapOptions: MapManagerOptions = {
        imageryLayers: [],
        baseLayer: new ShortCutClassicLayer(),
    };
    let mapManager: MapManager;
    let center: { lng: number; lat: number };

    let donuts: {
        from: number;
        to: number;
        n: number;
    }[] = [{ from: 0, to: 1500, n: 100 }];

    // hook events.
    hook.on("open", () => {
        mapManager = new MapManager(mapOptions);

        open = true;
    });
    hook.on("close", () => {
        open = false;
    });

    const modalClose = () => {
        hook.close(undefined);
    };

    let mapContainer: HTMLElement;
    let name: string;
    let description: string;
    let ready: boolean = false;

    afterUpdate(async () => {
        if (open) {
            if (mapManager.loaded()) return;

            const view = hook.getData();

            await mapManager.initialize(
                mapContainer,
                async () => {
                    mapManager.addControl(
                        new NavigationControl({}),
                        "top-right",
                    );
                    mapManager.addControl(
                        new FullscreenControl({}),
                        "top-right",
                    );

                    mapManager.addSource("locations", {
                        type: "geojson",
                        data: turf.featureCollection([]) as GeoJSON.GeoJSON,
                    });
                    mapManager.addSource("donuts", {
                        type: "geojson",
                        data: turf.featureCollection([]) as GeoJSON.GeoJSON,
                    });

                    mapManager.addLayer({
                        id: "locations-back",
                        type: "circle",
                        source: "locations",
                        layout: {
                            visibility: "visible",
                        },
                        paint: {
                            "circle-color": "#000000",
                            "circle-radius": 14,
                        },
                    });
                    mapManager.addLayer({
                        id: "locations",
                        type: "circle",
                        source: "locations",
                        layout: {
                            visibility: "visible",
                        },
                        paint: {
                            "circle-color": "#ffc000",
                            "circle-stroke-width": 0,
                            "circle-radius": 12,
                        },
                    });
                    mapManager.addLayer({
                        id: "locations-labels",
                        type: "symbol",
                        source: "locations",
                        minzoom: 8,
                        layout: {
                            visibility: "visible",
                            "text-field": "{name}",
                            "text-size": 15,
                        },
                        paint: {},
                    });

                    mapManager.addLayer(
                        {
                            id: "movement-lines",
                            type: "line",
                            source: "locations",
                            layout: {
                                "line-join": "round",
                                "line-cap": "round",
                            },
                            paint: {
                                "line-color": "#000",
                                "line-width": 2,
                                "line-offset": 8,
                                "line-opacity": 0.3,
                            },
                        },
                        "symbol",
                    );

                    mapManager.addLayer(
                        {
                            id: "donuts",
                            type: "fill",
                            source: "donuts",
                            paint: {
                                "fill-color": "#0080ff", // blue color fill
                                "fill-opacity": 0.5,
                            },
                        },
                        "symbol",
                    );
                    mapManager.on("click", (e) => {
                        center = e.lngLat;

                        matrix = {
                            name: "Random Locations",
                            locations: [],
                            elements: [],
                            properties: {
                                type: "one-to-many",
                                source: "random",
                            },
                        };
                    });
                },
                view,
            );
        }
    });

    let abortController: AbortController;
    async function updateLocations() {
        const localDonuts = [...donuts];
        localDonuts.sort((x, y) => x.from - y.from);

        if (typeof abortController !== "undefined") {
            abortController.abort();
        }
        abortController = new AbortController();
        const signal = abortController.signal;

        try {
            const result = await randomLocationsApi.getDonuts({
                location: center,
                donuts: localDonuts,
                signal: signal,
            });
            if (typeof result?.features === "undefined") return;

            matrix.locations.splice(0, matrix.locations.length);
            matrix.elements.splice(0, matrix.elements.length);
            if (oneToMany) {
                matrix.locations.push({
                    latitude: center.lat,
                    longitude: center.lng,
                    properties: {
                        type: "point-of-interest",
                    },
                });

                result.features.forEach((f) => {
                    const geometry = f.geometry as Point;
                    matrix.locations.push({
                        latitude: geometry.coordinates[1],
                        longitude: geometry.coordinates[0],
                        properties: {
                            type: "residential",
                        },
                    });

                    matrix.elements.push({
                        count: 1,
                        fromIdx: 0,
                        toIdx: matrix.locations.length - 1,
                    });
                });
            } else {
                matrix.locations.push({
                    latitude: center.lat,
                    longitude: center.lng,
                    properties: {
                        type: "point-of-interest",
                    },
                });

                result.features.forEach((f) => {
                    const geometry = f.geometry as Point;
                    matrix.locations.push({
                        latitude: geometry.coordinates[1],
                        longitude: geometry.coordinates[0],
                        properties: {
                            type: "residential",
                        },
                    });

                    matrix.elements.push({
                        count: 1,
                        toIdx: 0,
                        fromIdx: matrix.locations.length - 1,
                    });
                });
            }
        } catch (e) {
            if (signal.aborted) {
                //console.debug("aborted");
                return;
            } else {
                throw e;
            }
        }

        updateData();
        ready = true;
    }

    function updateData() {
        if (!mapManager.loaded()) return;

        const locationsSource = mapManager.getSource(
            "locations",
        ) as GeoJSONSource;
        if (typeof locationsSource !== "undefined") {
            locationsSource.setData(
                turf.featureCollection(
                    MatrixTools.buildFeatures(matrix),
                ) as unknown as GeoJSON.Feature<GeoJSON.Geometry>,
            );
        }

        const donutsSource = mapManager.getSource("donuts") as GeoJSONSource;
        if (typeof donutsSource !== "undefined") {
            if (matrix.locations.length > 0) {
                const location = center;

                const donutFeatures = turf.featureCollection([]);
                donuts.forEach((donut) => {
                    let circle: Feature<Polygon | MultiPolygon> = turf.circle(
                        [location.lng, location.lat],
                        donut.to,
                        {
                            units: "meters",
                            steps: 100,
                            properties: {
                                type: "donut",
                            },
                        },
                    );

                    if (donut.from > 0) {
                        const inner = turf.circle(
                            [location.lng, location.lat],
                            donut.from,
                            {
                                units: "meters",
                                steps: 100,
                                properties: {
                                    type: "donut",
                                },
                            },
                        );

                        circle = turf.difference(
                            turf.featureCollection([circle, inner]),
                        );
                    }

                    donutFeatures.features.push(circle);
                });

                donutsSource.setData(
                    donutFeatures as unknown as GeoJSON.Feature<GeoJSON.Geometry>,
                );
            }
        }
    }

    function save() {
        hook.close({ matrix, profile });
    }

    function addNew() {
        const max = Math.max(...donuts.map((x) => x.to));

        donuts.push({
            from: max,
            to: max + 1000,
            n: 30,
        });

        donuts = donuts;
    }

    $: if (
        donuts.length > 0 &&
        typeof matrix !== "undefined" &&
        typeof center !== "undefined" &&
        typeof oneToMany !== "undefined"
    ) {
        updateLocations();
    }
</script>

{#if open}
    <div class="modal show" style="display: block;">
        <div class="modal-dialog modal-fullscreen">
            <div class="modal-content">
                <div class="modal-header">
                    <div class="modal-title h4">
                        {$_("impact.scenario.randomPointsModal.title")}
                    </div>
                    <button
                        type="button"
                        class="btn-close btn-lg btn-close-lg"
                        aria-label="Close"
                        on:click={modalClose}
                    />
                </div>
                <div class="modal-body p-0">
                    <div class="container-fluid p-0 h-100">
                        <div class="row g-0 h-100">
                            <div class="col-8">
                                <div class="map w-100 h-100">
                                    <div
                                        class="w-100 h-100"
                                        bind:this={mapContainer}
                                    >
                                        <slot />
                                    </div>
                                </div>
                            </div>
                            <div class="col-4 p-2">
                                <div class="row align-items-center">
                                    <div class="col">
                                        <h4 class="mb-0">
                                            {$_(
                                                "impact.scenario.randomPointsModal.title",
                                            )}
                                        </h4>
                                        {$_(
                                            "impact.scenario.randomPointsModal.explanation",
                                        )}
                                    </div>
                                </div>
                                <div class="row align-items-center">
                                    <div class="col" />
                                    <div class="col-auto">
                                        <button
                                            class="btn btn-xs btn-primary"
                                            on:click={() => addNew()}
                                        >
                                            {$_(
                                                "impact.scenario.randomPointsModal.add",
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <DonutList bind:donuts />
                                <div class="row">
                                    <div class="col my-2">
                                        {$_(
                                            "impact.scenario.randomPointsModal.addRoutes",
                                        )}
                                        <select
                                            class="fs-tool form-select form-select-tool d-inline ps-2 profile-select"
                                            bind:value={profile}
                                        >
                                            {#each profiles as profile}
                                                <option value={`${profile}`}>
                                                    {`${profile}`}
                                                </option>
                                            {/each}
                                        </select>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col">
                                        <label
                                            class="my-2"
                                            bind:this={$oneToManyLabel}
                                        >
                                            <input
                                                bind:checked={oneToMany}
                                                type="checkbox"
                                            />
                                            {$_(
                                                "impact.scenario.randomPointsModal.onetomany.description",
                                            )}
                                            <TinyHelp />
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button
                        type="button"
                        class="btn btn-secondary {ready ? '' : 'disabled'}"
                        data-bs-dismiss="modal"
                        on:click={save}
                        >{$_("impact.scenario.randomPointsModal.save")}
                    </button>
                </div>
            </div>
        </div>
    </div>
{/if}

<style>
    .map {
        border-radius: 0.5rem;
        position: relative;
        bottom: 0px;
        left: 0px;
        right: 0px;
    }

    .profile-select {
        width: 150px;
    }

    .modal {
        display: block;
    }
</style>
